import { useCallback, useState } from 'react';

/**
 * Hook para gerenciar um conjunto de índices expandidos (ex: cards de log/auditoria).
 *
 * Equivalente Angular/Java: BehaviorSubject<Set<number>> em um service.
 *
 * Retorna:
 *  - `expandedSet`: Set imutável dos índices expandidos
 *  - `toggle(idx)`: alterna o índice
 *  - `expandAll(count)`: expande todos os `count` itens
 *  - `collapseAll()`: colapsa tudo
 */
export function useExpandSet() {
  const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set());

  const toggle = useCallback((idx: number) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(
    (count: number) => setExpandedSet(new Set(Array.from({ length: count }, (_, i) => i))),
    [],
  );

  const collapseAll = useCallback(() => setExpandedSet(new Set()), []);

  return { expandedSet, toggle, expandAll, collapseAll };
}
